// ============================================================================
// Send confirmation — the last thing between a draft and a client's inbox.
//
// The email composer lives at the top of the prep screen, collapsed. That
// optimised the wrong moment: the decision point is when you hit Send, not
// when you start building. Ben missed it entirely during smoke testing and
// sent the default copy without realising it was editable.
//
// So Send now opens this: the email as the recipient will actually receive
// it, who is getting it and in what order, and two ways out — edit, or send.
// Still two clicks for a sender who is happy with the default; it is just no
// longer possible to send without having seen what you are sending.
// ============================================================================

import { AlertTriangle, Loader2, Mail, PenLine, Send, X } from 'lucide-react';
import { overlayClickClose } from '../../shared/overlay-close';
import type { EnvelopeCopy, RecipientRow } from './envelope-lib';
import {
  MergeVariable, applyMerge, htmlToPreviewText, unknownVariables,
  unresolvedVariables,
} from './merge-vars';

export interface SendConfirmProps {
  envelopeTitle: string;
  senderName: string;
  copy: EnvelopeCopy;
  recipients: RecipientRow[];
  expirationDays: number;
  busy: boolean;
  error: string | null;
  onEditEmail: () => void;
  onSend: () => void;
  onClose: () => void;
}

export function SendConfirm({
  envelopeTitle, senderName, copy, recipients, expirationDays, busy, error,
  onEditEmail, onSend, onClose,
}: SendConfirmProps) {
  const signers = recipients.filter((r) => r.role !== 'CC');
  const ccs = recipients.filter((r) => r.role === 'CC');
  const first = signers[0];

  const vars: Partial<Record<MergeVariable, string>> = {
    recipient_name: first?.name ?? first?.email ?? '',
    document_name: envelopeTitle,
    sender_name: senderName,
    company_name: 'Terminal Cleaning Services',
    expiration_days: String(expirationDays),
    signing_link: 'https://app.terminalcleaning.com/sign/…',
  };

  const subject = copy.email_subject?.trim()
    ? applyMerge(copy.email_subject, vars)
    : `Signature requested — ${envelopeTitle}`;
  const usingDefaultBody = !copy.email_body?.trim();
  const body = usingDefaultBody
    ? `Hello ${first?.name ?? 'there'},\n\n${senderName} at Terminal Cleaning Services has requested your signature on ${envelopeTitle}.`
    : htmlToPreviewText(applyMerge(copy.email_body ?? '', vars));

  const raw = `${copy.email_subject ?? ''}\n${copy.email_body ?? ''}`;
  const problems = [...unknownVariables(raw), ...unresolvedVariables(raw, vars)];

  // Group by signing order so the sender sees the routing, not just a list.
  const orders = [...new Set(signers.map((r) => r.signing_order))].sort((a, b) => a - b);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-confirm-title"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-xl bg-tcs-raised p-5 sm:max-w-lg sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="send-confirm-title" className="text-lg font-semibold text-tcs-text">
            Send for signature
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-tcs-text-muted"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* ── who, and in what order ─────────────────────────────────────── */}
        <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
            Going to
          </p>
          <ol className="mt-1.5 space-y-1">
            {orders.map((o) => (
              <li key={o} className="flex items-start gap-2 text-sm text-tcs-text">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tcs-raised text-[10px] font-semibold">
                  {o}
                </span>
                <span>
                  {signers.filter((r) => r.signing_order === o)
                    .map((r) => `${r.name ?? r.email}${r.role === 'APPROVER' ? ' (approves)' : ''}`)
                    .join(' + ')}
                  {signers.filter((r) => r.signing_order === o).length > 1
                    ? ' — at the same time' : ''}
                  {signers.filter((r) => r.signing_order === o)
                    .some((r) => (r.auth_mode ?? 'emailed_link') === 'email_otp') && (
                    <span className="ml-1 text-[11px] text-st-warn-fg">
                      · must enter an emailed code
                    </span>
                  )}
                </span>
              </li>
            ))}
            {ccs.length > 0 && (
              <li className="flex items-start gap-2 text-sm text-tcs-text-muted">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tcs-raised text-[10px]">
                  cc
                </span>
                <span>{ccs.map((r) => r.name ?? r.email).join(', ')} — copy when complete</span>
              </li>
            )}
          </ol>
          <p className="mt-2 text-[11px] text-tcs-text-muted">
            Only the first group is emailed now. Everyone else is notified when
            it&rsquo;s their turn. Links last {expirationDays} days.
          </p>
        </div>

        {/* ── the actual email ───────────────────────────────────────────── */}
        <div className="mt-3 rounded-md border border-tcs-border p-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
            <Mail className="h-3.5 w-3.5" aria-hidden />
            What they&rsquo;ll receive
            {usingDefaultBody && (
              <span className="font-normal normal-case tracking-normal">
                · standard wording
              </span>
            )}
          </p>
          <p className="mt-1.5 text-sm font-semibold text-tcs-text">{subject}</p>
          <p className="mt-1 whitespace-pre-wrap text-xs text-tcs-text-muted">{body}</p>
          {copy.note_to_signer?.trim() && (
            <div className="mt-2 border-l-4 border-tcs-accent-ink bg-[#F4F7FB] px-2 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                Note from {senderName}
              </p>
              <p className="whitespace-pre-wrap text-xs text-tcs-text">
                {copy.note_to_signer}
              </p>
            </div>
          )}
          <p className="mt-2 text-xs text-tcs-accent-ink underline">
            Review and sign the document
          </p>
        </div>

        {problems.length > 0 && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-st-warn-fg" aria-hidden />
            <p className="text-xs text-st-warn-fg-strong">
              <strong>Check the wording:</strong>{' '}
              {problems.map((p) => `{{${p}}}`).join(', ')} won&rsquo;t be filled
              in and will go out exactly as written.
            </p>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-2 text-sm text-st-danger-fg">{error}</p>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
          <button
            type="button"
            onClick={onSend}
            disabled={busy}
            className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-1.5 rounded-md bg-tcs-accent-ink px-4 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              : <Send className="h-4 w-4" aria-hidden />}
            {busy ? 'Sending…' : 'Looks right — send it'}
          </button>
          <button
            type="button"
            onClick={onEditEmail}
            disabled={busy}
            className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-1.5 rounded-md border border-tcs-border px-4 text-sm font-medium text-tcs-text disabled:opacity-50"
          >
            <PenLine className="h-4 w-4" aria-hidden />
            Edit the email first
          </button>
        </div>
      </div>
    </div>
  );
}
